import { Flex, Spacing, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import * as Styled from '../../styles'
import { CompanyInfo } from '../../utils'
import { CompanyNameTooltipIcon } from '../CompanyNameTooltipIcon'

interface MatchedInformationProps {
  values: CompanyInfo
  missingFields: string[]
}

export const MatchedInformation = ({ values, missingFields }: MatchedInformationProps): ReactElement | null => {
  const { t } = useTranslation()

  // Note that companyCity is not here
  // Since that info is almost always present
  const hasValues = Boolean(
    values.companyName ||
      values.accountHolderFirstName ||
      values.accountHolderLastName ||
      values.companyAddress ||
      values.companyZipCode,
  )

  const hasCompanyName = !missingFields.includes('companyName')
  const hasAccountHolderName = !(
    missingFields.includes('accountHolderFirstName') || missingFields.includes('accountHolderLastName')
  )
  const hasCompanyAddress = !(
    missingFields.includes('companyAddress') ||
    missingFields.includes('companyZipCode') ||
    missingFields.includes('companyCity')
  )

  const hasInfo = hasCompanyName || hasAccountHolderName || hasCompanyAddress

  // Can happen for companies with "minimal" information
  // We need to check for both booleans because of an edge case
  // in the state management, where "when searching" the missingFields
  // is an empty array, so we display the undefined values
  if (!hasInfo || !hasValues) {
    return null
  }

  return (
    <Flex flexDirection="column" gap={Spacing.S8}>
      <Text weight="bold" colorVariant="secondary">
        {t('e_invoice.kyc.step.company_details.matching_information.label').toUpperCase()}
      </Text>
      <Styled.InformationCard>
        {hasCompanyName && (
          <Flex flexDirection="column" alignItems="flex-start" justifyContent="center">
            <Flex alignItems="center" gap={Spacing.S4}>
              <Text variant="bodySM" colorVariant="secondary">
                {t('e_invoice.kyc.step.company_details.form.company_name.label')}
              </Text>

              <CompanyNameTooltipIcon />
            </Flex>
            <Text>{values.companyName}</Text>
          </Flex>
        )}

        {hasAccountHolderName && (
          <Flex flexDirection="column" alignItems="flex-start" justifyContent="center">
            <Text variant="bodySM" colorVariant="secondary">
              {t('e_invoice.kyc.step.company_details.form.first_name.label')}
            </Text>
            <Text>{`${values.accountHolderFirstName} ${values.accountHolderLastName}`}</Text>
          </Flex>
        )}
        {hasCompanyAddress && (
          <Flex flexDirection="column" alignItems="flex-start" justifyContent="center">
            <Text variant="bodySM" colorVariant="secondary">
              {t('e_invoice.kyc.step.company_details.form.company_address.label')}
            </Text>
            <Text>{values.companyAddress}</Text>
            <Text>{`${values.companyZipCode}, ${values.companyCity}`}</Text>
          </Flex>
        )}
      </Styled.InformationCard>
    </Flex>
  )
}
